import { createAction } from '@reduxjs/toolkit'
import { all, call, put, select, takeLatest } from 'redux-saga/effects'

import { modifyMaterialBasedOnConflicts } from './insertEstimatedOptionOverlay'
import { createModification } from '../../../api/projects-api'
import { IMaterialModificationConflictAPI } from '../../../models/masterSetPlan'
import { isNonAreaJoistLine } from '../../../utils/project/project-helper-functions'
import managers from '../../lib/managers'
import PaperManager from '../../lib/managers/PaperManager'
import { Cutout, Label, Select, Workspace } from '../../lib/toolBoxes/2D'
import addSelectFunctionalityToDrawable from '../../lib/utils/functionality-bindings/addSelectFunctionalityToDrawable'
import { selectCurrentOverlayChunkIds, setEstimatedOptionOverlayChunkId, setOverlayChunkId } from '../../slices/2D'
import {
    resetMaterialModificationConflicts,
    selectMaterialModificationConflicts,
    updateConflictResolutionFlag,
    updateReviewingConflictsFlag,
    updateSavedMaterialModification,
} from '../../slices/masterSetPlan'
import { selectProjectId } from '../../slices/project'

export const createModificationAction = createAction('createModification')

export function* handleModificationCreation() {
    const manager: PaperManager | null = yield call(managers.get2DManager)
    const projectId: number | null = yield select(selectProjectId)
    const overlayChunkIds: number[] = yield select(selectCurrentOverlayChunkIds)

    if (!manager || !projectId || !overlayChunkIds.length) return

    const [selectTool, workspaceTool, cutoutTool, labelTool]: [Select, Workspace, Cutout, Label] = yield call(
        manager.getTools,
        [Select.NAME, Workspace.NAME, Cutout.NAME, Label.NAME]
    )

    yield call(selectTool.exitSelectMode)

    const { materialModificationConflictsToSave }: ReturnType<typeof selectMaterialModificationConflicts> =
        yield select(selectMaterialModificationConflicts)

    if (materialModificationConflictsToSave?.length) {
        const mods: IMaterialModificationConflictAPI[] = yield call(
            createModification,
            materialModificationConflictsToSave,
            projectId
        )

        yield all(
            mods.map((mod) => {
                return put(
                    updateSavedMaterialModification({
                        id: mod.id,
                        material_location_id: mod.material_location_id,
                        coordinates: mod.coordinates,
                        floor_plan_id: mod.floor_plan_id,
                        option_ids: mod.option_ids.split('_').map((opId) => parseInt(opId)),
                        cutouts: mod.cutouts,
                        shapeColor: mod.shapeColor,
                    })
                )
            })
        )
    }

    yield put(resetMaterialModificationConflicts())

    yield put(updateConflictResolutionFlag(true))
    yield put(updateReviewingConflictsFlag(false))

    const visibilityTest = (item: paper.Item): boolean => !isNonAreaJoistLine(item)

    const allPaperDrawablesOnPage: paper.Item[] = yield call(
        workspaceTool.getItemsWithCriteria,
        'data',
        function (data: any) {
            return data?.drawable_id || data?.aiSuggestion?.id
        }
    )

    yield all(
        allPaperDrawablesOnPage.map((item) =>
            call(
                (item: paper.Item, visTest: (item: paper.Item) => boolean) => {
                    item.visible = visTest(item)
                },
                item,
                visibilityTest
            )
        )
    )

    allPaperDrawablesOnPage.forEach((item) => {
        if (overlayChunkIds.includes(item.data.document_chunk_id)) {
            item.data.isMultiSelectable = true
            item.data.selectable = true
        } else {
            item.data.selectable = false
            item.data.isMultiSelectable = false
        }
    })

    // Safe to call once per overlay -- it filters savedMaterialModifications by option_ids.
    for (const overlayChunkId of overlayChunkIds) {
        yield call(modifyMaterialBasedOnConflicts, workspaceTool, labelTool, overlayChunkId)
    }

    yield call([cutoutTool, 'setItemMetadataUpdateFunction'], addSelectFunctionalityToDrawable)

    yield put(setOverlayChunkId(null))
    yield put(
        setEstimatedOptionOverlayChunkId({
            opt: overlayChunkIds[overlayChunkIds.length - 1],
            additionalOpts: overlayChunkIds.slice(0, -1),
            base: null,
        })
    )
}

export function* watchForModificationCreation() {
    yield takeLatest(createModificationAction.type, handleModificationCreation)
}
